import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import DetailTable from 'components/DetailTable';
import { ConfigItem } from 'components/DetailTable/types';
import LoadingScreen from 'components/LoadingScreen';
import { useAtomValue } from 'jotai';
import { isAdminUserLoggedIn } from 'lib/helper';
import { showFailureToast } from 'lib/ToastHelper';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Row, Col, Button, ButtonGroup, Input } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { fetchIntegrationDetails } from '../../services/integrationListingService';
import {
  BenchmarkScanItem,
  IntegrationItem,
} from '../../types/integrationListing.types';

interface IntegrationDetailModalProps {
  id: number;
  onClose: () => void;
}

const IntegrationDetailModal: React.FC<IntegrationDetailModalProps> = ({
  id,
  onClose,
}) => {
  const companyId = useAtomValue(companyIdAtom);
  const [data, setData] = useState<IntegrationItem | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredBenchmarkScans, setFilteredBenchmarkScans] = useState<
    BenchmarkScanItem[]
  >([]);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    void fetchIntegrationDetails(id, companyId)
      .then((res) => {
        if (res.status === 200 && res.data) {
          setData(res.data);
          if (res.data.benchmark_scans) {
            setFilteredBenchmarkScans(res.data.benchmark_scans);
          }
        }
      })
      .catch((err) => {
        const message =
          err?.data?.detail ??
          'Failed to load integration details. Please try again.';
        showFailureToast(message);
        onCloseRef.current();
      })
      .finally(() => setIsLoading(false));
  }, [id, companyId]);

  const integrationDetailData = data
    ? {
        ...data,
        display_status: data.is_deleted
          ? 'Deleted'
          : data.is_active
            ? 'Active'
            : 'Inactive',
      }
    : data;

  const detailConfig: ConfigItem[] = [
    { name: 'Provider', key: 'provider_code', type: 'string' },
    { name: 'Module', key: 'module_code', type: 'string' },
    { name: 'Region', key: 'region', type: 'string' },
    { name: 'Integration Level', key: 'integration_type', type: 'string' },
    { name: 'Integration Name', key: 'integration_label', type: 'string' },
    { name: 'Integration ID', key: 't12_integration_id', type: 'string' },
    { name: 'Account ID', key: 'account_id', type: 'string' },
    { name: 'External ID', key: 'external_id', type: 'string' },
    { name: 'Status', key: 'display_status', type: 'string' },
    { name: 'Created At', key: 'created_at', type: 'date' },
    { name: 'Updated At', key: 'updated_at', type: 'date' },
    { name: 'Last Synced At', key: 'last_sync_at', type: 'date' },
    ...(data?.is_deleted
      ? [
          { name: 'Deleted At', key: 'delete_at', type: 'date' as const },
          { name: 'Deleted By', key: 'delete_by', type: 'string' as const },
          {
            name: 'Open Findings at Deletion',
            key: 'open_vuln_counts_at_deletion',
            type: 'number' as const,
          },
          {
            name: 'Closed Findings at Deletion',
            key: 'closed_vuln_counts_at_deletion',
            type: 'number' as const,
          },
        ]
      : []),
  ];

  const errorConfig: ConfigItem[] = [
    { name: 'Error Details', key: 'error_details', type: 'string' },
  ];

  const latestScanConfig: ConfigItem[] = [
    { name: 'Benchmark', key: 'benchmark', type: 'string' },
    { name: 'Scan Type', key: 'trigger_type', type: 'string' },
    { name: 'Status', key: 'status', type: 'string' },
    { name: 'Job Type', key: 'job_type', type: 'string' },
    { name: 'Region', key: 'region', type: 'string' },
    { name: 'Started At', key: 'started_at', type: 'date' },
    { name: 'Finished At', key: 'finished_at', type: 'date' },
    { name: 'Scan Duration', key: 'scan_duration', type: 'string' },
  ];

  const benchmarkScanConfig: ConfigItem[] = [
    {
      name: '',
      key: 'benchmark_scan.list',
      type: 'list',
      listConfig: {
        childNode: undefined,
        config: [
          { name: 'Benchmark', key: 'benchmark', type: 'string' },
          { name: 'Region', key: 'region', type: 'string' },
          { name: 'Last Scan Date', key: 'last_scan_date', type: 'date' },
        ],
      },
    },
  ];

  const tabs = [
    {
      title: 'Integration Details',
      config: detailConfig,
      data: integrationDetailData,
    },
    ...(data?.error_details && isAdminUserLoggedIn()
      ? [{ title: 'Error Info', config: errorConfig, data: data }]
      : []),
    ...(data?.latest_scan
      ? [
          {
            title: 'Last Scan',
            config: latestScanConfig,
            data: data.latest_scan,
          },
        ]
      : []),
    ...(data?.benchmark_scans
      ? [
          {
            title: 'Benchmark Scan',
            config: benchmarkScanConfig,
            data: { benchmark_scan: { list: filteredBenchmarkScans } },
          },
        ]
      : []),
  ];

  function handleSearch(value: string): void {
    if (!data?.benchmark_scans) return;
    const filtered = data.benchmark_scans.filter((item: BenchmarkScanItem) =>
      item.benchmark.toLowerCase().includes(value.toLowerCase()),
    );
    setFilteredBenchmarkScans(filtered);
  }

  const benchmarkScanTabIndex = tabs.findIndex(
    (tab) => tab.title === 'Benchmark Scan',
  );

  if (isLoading) return <LoadingScreen />;

  return (
    <CustomModalWrapper id="integration-scroll">
      {data && (
        <Fragment>
          <Row>
            <Col sm="6" md="4" lg="3">
              <ButtonGroup
                vertical
                className="btn-group-toggle"
                style={{ maxWidth: '100%', width: '100%' }}
              >
                {tabs.map((tab, index) => (
                  <Button
                    key={`tab-btn-${index}`}
                    tag="label"
                    style={{
                      padding: '10px',
                      margin: '0px',
                      textAlign: 'left',
                    }}
                    color={activeTab === index ? 'primary' : 'secondary'}
                    size="sm"
                    title={tab.title}
                    onClick={() => setActiveTab(index)}
                  >
                    {tab.title}
                  </Button>
                ))}
              </ButtonGroup>
            </Col>
            <Col sm="12" md="8" lg="9">
              {activeTab === benchmarkScanTabIndex &&
                benchmarkScanTabIndex !== -1 && (
                  <Input
                    className="mb-2"
                    name="search"
                    maxLength={255}
                    placeholder="Search"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchQuery(value);
                      handleSearch(value);
                    }}
                  />
                )}
              {tabs.map(
                (tab, index) =>
                  activeTab === index && (
                    <DetailTable
                      key={index}
                      data={tab.data}
                      config={tab.config as ConfigItem[]}
                    />
                  ),
              )}
            </Col>
          </Row>
        </Fragment>
      )}
    </CustomModalWrapper>
  );
};

export default IntegrationDetailModal;
